import React, { useState, useRef, useEffect } from "react";
import * as S from "./styles";
import { useDispatch, useSelector } from "react-redux";
import {
  PresignedUrlProps,
  removeRequiredCardId,
  setRequiredCardId,
  setTitle,
  StateProps,
  uploadPresignedUrl,
} from "../../store";

import Cropper from "react-cropper";
import "cropperjs/dist/cropper.css";
import CheckImg from "../../assets/checkImg.png";
import XImg from "../../assets/xImg.png";
import { ImageDelete } from "./styles";
import axios from "axios";
import { MdAddPhotoAlternate } from "react-icons/md";

type ReactCropperElement = HTMLImageElement & {
  cropper: Cropper;
};

const AddTitleImage = ({ id, image }: { id: string; image: string }) => {
  const dispatch = useDispatch();
  const [src, setSrc] = useState<string | null>(null);
  const [croppedImage, setCroppedImage] = useState<string | null>(image);
  const cropperRef = useRef<ReactCropperElement>(null); // ReactCropperElement 타입 사용

  const requiredCardIds = useSelector((state: StateProps) => state.required as string[]);

  const needToCompleteRequired = requiredCardIds.includes(id);

  // 확대/축소 비율 상태 추가
  const [zoom, setZoom] = useState<number>(1);

  // 인터벌 상태 추가
  const moveInterval = useRef<NodeJS.Timeout | null>(null);
  const zoomInterval = useRef<NodeJS.Timeout | null>(null);

  const handleImageChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setSrc(reader.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleCrop = async () => {
    const cropper = cropperRef.current?.cropper;
    if (cropper) {
      cropper.crop();
      const canvas = cropper.getCroppedCanvas();
      const { width } = canvas;

      let croppedDataUrl;
      if (width > 1000) {
        croppedDataUrl = cropper.getCroppedCanvas({ width: 1000 }).toDataURL();
      } else {
        croppedDataUrl = cropper.getCroppedCanvas().toDataURL();
      }

      const file = dataURLtoFile(croppedDataUrl, "titleImage.png");

      const fileSize = file.size / 1024 / 1024;

      if (fileSize > 1) {
        alert("이미지 크기는 1MB를 초과할 수 없습니다.");
        setSrc(null);
        return;
      }

      const uploadUrl = await uploadPresignedUrl(file);

      setCroppedImage(uploadUrl);
      dispatch(setTitle({ cardId: id, text: uploadUrl }));
      setSrc(null); // Cropper 창을 닫기 위해 src를 null로 설정
      dispatch(removeRequiredCardId({ cardId: id }));
    }
  };

  const dataURLtoFile = (dataurl: string, filename: string) => {
    const arr = dataurl.split(",");
    const mime = arr[0].match(/:(.*?);/)![1];
    const bstr = atob(arr[1]);
    let n = bstr.length;
    const u8arr = new Uint8Array(n);
    // eslint-disable-next-line no-plusplus
    while (n--) {
      u8arr[n] = bstr.charCodeAt(n);
    }
    return new File([u8arr], filename, { type: mime });
  };

  const handleDeleteCrop = () => {
    setSrc(null);
  };

  const handleDeleteImage = () => {
    dispatch(setTitle({ cardId: id, text: "" }));
    setCroppedImage(null);
  };

  // 확대/축소 핸들러 함수 추가
  const handleZoom = (zoomAmount: number) => {
    const cropper = cropperRef.current?.cropper;
    if (cropper) {
      cropper.zoom(zoomAmount);
      setZoom(cropper.getData().scaleX);
    }
  };

  const startZooming = (zoomAmount: number) => {
    handleZoom(zoomAmount);
    zoomInterval.current = setInterval(() => {
      handleZoom(zoomAmount);
    }, 100);
  };

  const stopZooming = () => {
    if (zoomInterval.current) {
      clearInterval(zoomInterval.current);
    }
  };

  // 이미지 이동 핸들러 함수 추가
  const handleMove = (x: number, y: number) => {
    const cropper = cropperRef.current?.cropper;
    if (cropper) {
      cropper.move(x, y);
    }
  };

  const startMoving = (x: number, y: number) => {
    handleMove(x, y);
    moveInterval.current = setInterval(() => {
      handleMove(x, y);
    }, 100);
  };

  const stopMoving = () => {
    if (moveInterval.current) {
      clearInterval(moveInterval.current);
    }
  };

  return (
    <S.Container alert={needToCompleteRequired} hasSrc={src !== null}>
      {croppedImage && !src ? (
        <S.TitleHighlight image={croppedImage}>
          <S.DeleteButton onClick={handleDeleteImage}>×</S.DeleteButton>
        </S.TitleHighlight>
      ) : null}
      {!croppedImage && !src ? (
        <>
          <MdAddPhotoAlternate size={40} />
          {/* <S.PlusIcon /> */}
          <S.HiddenInput
            type="file"
            accept="image/*"
            id="titleImage"
            onChange={handleImageChange}
          />
          <S.Label htmlFor="titleImage" />
        </>
      ) : null}
      {src && (
        <>
          <div>
            <Cropper
              src={src}
              style={{ height: "400px", width: "600px" }}
              aspectRatio={4.26}
              guides={false}
              ref={cropperRef}
              zoomable
            />
          </div>
          <S.ImageCheck backgroundImage={CheckImg} onClick={handleCrop} />
          <S.ImageDelete backgroundImage={XImg} onClick={handleDeleteCrop} />
          <S.ZoomButtonContainer>
            <S.ZoomButton
              type="button"
              onMouseDown={() => startZooming(0.1)}
              onMouseUp={stopZooming}
              onMouseLeave={stopZooming}
            >
              +
            </S.ZoomButton>
            <S.ZoomButton
              type="button"
              onMouseDown={() => startZooming(-0.1)}
              onMouseUp={stopZooming}
              onMouseLeave={stopZooming}
            >
              -
            </S.ZoomButton>
          </S.ZoomButtonContainer>
          <S.MoveButtonContainer>
            <S.MoveButton
              type="button"
              onMouseDown={() => startMoving(0, 10)}
              onMouseUp={stopMoving}
              onMouseLeave={stopMoving}
              top={90}
              right={15}
            >
              ↑
            </S.MoveButton>
            <S.MoveButton
              type="button"
              onMouseDown={() => startMoving(10, 0)}
              onMouseUp={stopMoving}
              onMouseLeave={stopMoving}
              top={110}
              right={30}
            >
              ←
            </S.MoveButton>
            <S.MoveButton
              type="button"
              onMouseDown={() => startMoving(-10, 0)}
              onMouseUp={stopMoving}
              onMouseLeave={stopMoving}
              top={110}
              right={0}
            >
              →
            </S.MoveButton>
            <S.MoveButton
              type="button"
              onMouseDown={() => startMoving(0, -10)}
              onMouseUp={stopMoving}
              onMouseLeave={stopMoving}
              top={130}
              right={15}
            >
              ↓
            </S.MoveButton>
          </S.MoveButtonContainer>
        </>
      )}
    </S.Container>
  );
};

export default AddTitleImage;
